// @flow
/* eslint-disable no-shadow */
import classnames from 'classnames';
import React, { useState } from 'react';
import Toggle from 'react-toggled';
import { withStyles } from '@material-ui/core/styles';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import List from '@material-ui/core/List';
import ListItemText from '@material-ui/core/ListItemText';
import Drawer from '@material-ui/core/Drawer';
import ListItem from '@material-ui/core/ListItem';
import Divider from '@material-ui/core/Divider';
import Help from '@material-ui/icons/Help';
import { withTranslation } from 'react-i18next';
import ViewAgendaOutlinedIcon from '@material-ui/icons/ViewAgendaOutlined';
import ReportOutlinedIcon from '@material-ui/icons/ReportOutlined';
import KeyboardOutlinedIcon from '@material-ui/icons/KeyboardOutlined';
import BookOutlinedIcon from '@material-ui/icons/BookOutlined';
import CachedOutlinedIcon from '@material-ui/icons/CachedOutlined';
import LangSelectorContainer from '../../modules/scribe/LangSelectorContainer';
import TranslationSelector from '../../modules/scribe/TranslationSelector';
import ScribeReportModalContainer from '../../modules/scribe/ScribeReportModalContainer';
import ScribeSpeechTrainingSelectorContainer from '../../modules/scribe/ScribeSpeechTrainingSelectorContainer';
import ScribeShortcutModal from '../../modules/scribe/ScribeShortcutModal';
import { getIsMac } from '../../utils/electron';
import { styles, EmptyIcon } from '../../modules/scribe/SidebarHelpers';
import ConversationModeSelector from '../../modules/scribe/ConversationModeSelector';
import OpenOrDownloadAvaAppComponent from '../OpenOrDownloadAvaAppComponent';
import { canTranslate } from '../../utils/status';
import LanguageSwitcher from './LanguageSwitcher';
import User from './User';
import { show } from '../../utils/intercom';
import { shouldShowNewSidebarAndHeader } from '../../utils/layout';

import {
  firebaseLogout,
  getTranscriptText,
  switchLang,
  toggleAudioMode,
  toggleDevMode,
  toggleSideBar,
  updateBoosting,
  updateFullScreen,
} from '../../actions';
import LogOut from './LogOut';
import ServerSettings from './ServerSettings';
import CaptionsSettings from './CaptionsSettings';
import OpenOrDownloadAva from './OpenOrDownloadAva';
import SelectMicrophone from './SelectMicrophone';
import useNetwork from '../../hooks/useNetwork';

import HomeView from './HomeView';
import ScribePreferences from './ScribePreferences';
import Link from '@material-ui/core/Link';

function index({
  classes,
  displayable,
  features,
  firebaseUser,
  history,
  i18n,
  intercomDisplayed,
  isInConversation,
  sidebarShown,
  canTranslate,
  t,
  toggleSideBar,
  updateBoosting,
  className,
  electronCaptionMode,
}: {
  allowSaving: boolean,
  classes: Object,
  conversations: boolean,
  displayable: Object,
  features: Object,
  firebaseLogout: Function,
  firebaseUser: Object,
  fullScreen: boolean,
  getTranscriptText: Function,
  history: Object,
  i18n: Function,
  intercomDisplayed: boolean,
  inTwilioCalls: boolean,
  isInConversation: boolean,
  lang: string,
  langs: Array<string>,
  muted: boolean,
  scribe: string,
  sidebarShown: boolean,
  canTranslate: boolean,
  switchLang: Function,
  t: Object,
  theme: Object,
  toggleAudioMode: Function,
  toggleDevMode: Function,
  toggleSideBar: Function,
  updateBoosting: Function,
  updateFullScreen: Function,
  user: Object,
  className?: string,
  electronCaptionMode: boolean,
}) {
  const [openOrDownloadAvaApp, setOpenOrDownloadAvaApp] = useState(false);
  const online = useNetwork();

  let marginTop = 67;
  if (window.isElectron) {
    if (getIsMac()) {
      marginTop += 28;
    } else {
      marginTop += 32;
    }
  }
  if (!online && !isInConversation) {
    marginTop += 60;
  }

  const showNewSidebarAndHeader = shouldShowNewSidebarAndHeader({
    electronCaptionMode,
    firebaseUser,
    history,
    isInConversation,
  });
  if (showNewSidebarAndHeader) {
    return <HomeView className={classnames(className)} />;
  }

  return (
    <Drawer
      classes={{
        paper: classes.drawerPaper,
      }}
      className={classnames('invisible-scrollbar', className)}
      open={sidebarShown}
      onClose={toggleSideBar}
      anchor="left"
      variant="persistent"
      style={{
        width: sidebarShown && i18n.language === 'fr' ? 300 : null,
      }}
      PaperProps={{
        style: {
          width: sidebarShown && i18n.language === 'fr' ? 300 : null,
          height: `calc(100% - ${marginTop}px)`,
          marginTop,
          visibility: 'visible',
        },
      }}
    >
      <List component="nav" disablePadding>
        <User />
        <>
          {displayable.scribeDashboard && (
            <Link to="/web/scribe-dashboard">
              <ListItem key="scribe_dashboard_toggle" role="button" button>
                <ListItemIcon>
                  <ViewAgendaOutlinedIcon />
                </ListItemIcon>
                <ListItemText classes={{ root: classes.listItemText }} primary={t('sidebar.scribeDashboard')} />
              </ListItem>
            </Link>
          )}
        </>
        <Divider />
        <OpenOrDownloadAva setOpenOrDownloadAvaApp={setOpenOrDownloadAvaApp} />
        <SelectMicrophone />
        <CaptionsSettings />
        <ScribePreferences />
        <ConversationModeSelector />
        {isInConversation && displayable.audio && <LangSelectorContainer />}
        {isInConversation && canTranslate && <TranslationSelector />}
        {features.scribe && <ScribeSpeechTrainingSelectorContainer classes={classes} />}
        {features.scribe && <Divider />}
        {features.scribe && [
          <Toggle key="1_toggle_array">
            {({ on, getTogglerProps, setOff }) => (
              <ListItem button>
                <ListItemIcon>
                  <ReportOutlinedIcon />
                </ListItemIcon>
                <ListItemText
                  classes={{ root: classes.listItemText }}
                  {...getTogglerProps()}
                  primary={t('sidebar.reportIssue')}
                />
                <ScribeReportModalContainer open={on} close={setOff} old={false} />
              </ListItem>
            )}
          </Toggle>,
        ]}
        {features.scribe && (
          <Toggle key="2_toggle_array">
            {({ on: on2, getTogglerProps: getTogglerProps2, setOff: setOff2 }) => (
              <ListItem button>
                <ListItemIcon>
                  <KeyboardOutlinedIcon />
                </ListItemIcon>
                <ListItemText
                  classes={{ root: classes.listItemText }}
                  {...getTogglerProps2()}
                  primary={t('sidebar.viewShortcutList')}
                />
                <ScribeShortcutModal open={on2} close={setOff2} />
              </ListItem>
            )}
          </Toggle>
        )}
        {features.scribe && (
          <ListItem
            button
            onClick={() => {
              window.open(
                'https://www.notion.so/ava/Scribe-guide-fr-translated-en-7f83c1e0158b4df3b9a5dbbeca3143f0',
                '_blank'
              );
            }}
          >
            <ListItemIcon>
              <BookOutlinedIcon />
            </ListItemIcon>
            <ListItemText classes={{ root: classes.listItemText }} primary={t('sidebar.scribeUserGuide')} />
          </ListItem>
        )}
        {features.scribe && (
          <ListItem key="boost" button onClick={() => updateBoosting(true)}>
            <ListItemIcon>
              <CachedOutlinedIcon />
            </ListItemIcon>
            <ListItemText classes={{ root: classes.listItemText }} primary={t('Boost')} />
          </ListItem>
        )}
        {!intercomDisplayed && (
          <ListItem button>
            <ListItemIcon>
              <Help />
            </ListItemIcon>
            <ListItemText
              classes={{ root: classes.listItemText }}
              primary={t('sidebar.contactSupport')}
              onClick={() => {
                show();
                toggleSideBar();
              }}
            />
          </ListItem>
        )}

        <ServerSettings />
        <Divider />
        <LogOut />
        <LanguageSwitcher />
      </List>
      {openOrDownloadAvaApp && <OpenOrDownloadAvaAppComponent />}
    </Drawer>
  );
}

const mapStateToProps = ({
  scribeConversation: { conversationStarted, inTwilioCalls, isInConversation, lang, langs, muted, scribe, status },
  scribeSession: {
    conversations,
    backends,
    captioner,
    currentPlatform,
    displayable,
    features,
    intercomDisplayed,
    subscription,
  },
  firebase: { user },
  scribeTranscriptUI: { fullScreen, electronCaptionMode },
}) => ({
  conversations,
  canTranslate: canTranslate(subscription, status),
  backends,
  conversationStarted,
  currentPlatform,
  displayable,
  features,
  firebaseUser: user,
  fullScreen,
  intercomDisplayed,
  inTwilioCalls,
  isInConversation,
  lang,
  langs,
  muted,
  scribe,
  user: captioner,
  electronCaptionMode,
});

export default connect(mapStateToProps, {
  firebaseLogout,
  getTranscriptText,
  switchLang,
  toggleAudioMode,
  toggleDevMode,
  toggleSideBar,
  updateBoosting,
  updateFullScreen,
})(withTranslation()(withStyles(styles, { withTheme: true })(withRouter(index))));
